'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

const DEMO_ACCOUNTS = [
  { label: 'Super Admin', email: 'superadmin@betaland.id', password: 'superadmin123', role: 'super_admin', redirect: '/admin-dashboard', icon: 'ShieldCheckIcon', color: 'text-red-500' },
  { label: 'Admin', email: 'admin@betaland.id', password: 'admin123', role: 'admin', redirect: '/admin-dashboard', icon: 'CogIcon', color: 'text-primary' },
  { label: 'Partner', email: 'partner@betaland.id', password: 'partner123', role: 'agent', redirect: '/partner/dashboard', icon: 'UserGroupIcon', color: 'text-accent' },
];

function getRoleRedirect(role: string | undefined | null): string {
  switch (role) {
    case 'super_admin':
      return '/admin-dashboard';
    case 'admin': case'finance': case'marketing':
      return '/admin-dashboard';
    case 'agent':
      return '/partner/dashboard';
    default:
      return '/partner/dashboard';
  }
}

function getRolePlatformLabel(role: string | undefined | null): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin Platform';
    case 'admin':
      return 'Admin Platform';
    case 'finance':
      return 'Finance Platform';
    case 'marketing':
      return 'Marketing Platform';
    case 'agent':
      return 'Partner Platform';
    default:
      return 'Platform';
  }
}

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchProfileAndRedirect = async (userId: string) => {
    const supabase = createClient();
    const { data: profileData } = await supabase
      .from('user_profiles')
      .select('role, email_activated')
      .eq('id', userId)
      .maybeSingle();

    const role = profileData?.role;

    // Block partner login if email not yet activated
    if (role === 'agent' && profileData?.email_activated === false) {
      await supabase.auth.signOut();
      throw new Error('EMAIL_NOT_ACTIVATED');
    }

    const redirect = getRoleRedirect(role);
    router.push(redirect);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const data = await signIn(email, password);
      const userId = data?.user?.id;
      if (userId) {
        await fetchProfileAndRedirect(userId);
      } else {
        router.push('/partner/dashboard');
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg === 'EMAIL_NOT_ACTIVATED') {
        setError('Akun Anda belum diaktifkan. Silakan cek email Anda dan klik link aktivasi yang telah dikirimkan.');
      } else if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
        setError('Email atau password salah. Silakan periksa kembali.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setError('Email belum dikonfirmasi. Silakan cek inbox Anda.');
      } else if (msg.toLowerCase().includes('too many requests')) {
        setError('Terlalu banyak percobaan login. Coba lagi beberapa saat.');
      } else {
        setError(msg || 'Login gagal. Silakan coba lagi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async (demo: typeof DEMO_ACCOUNTS[0]) => {
    setEmail(demo.email);
    setPassword(demo.password);
    setError('');
    setIsLoading(true);
    try {
      const data = await signIn(demo.email, demo.password);
      const userId = data?.user?.id;
      if (userId) {
        await fetchProfileAndRedirect(userId);
      } else {
        router.push(demo.redirect);
      }
    } catch (err: any) {
      setError(err?.message || 'Login demo gagal. Pastikan akun demo sudah dibuat di database.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <AppLogo size={64} />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Masuk ke Platform</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Admin, Super Admin &amp; Partner Betaland
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@betaland.id"
                required
                autoComplete="email"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full px-3 py-2.5 pr-10 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-1"
            >
              {isLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  Memverifikasi...
                </>
              ) : (
                <>
                  <Icon name="ArrowRightOnRectangleIcon" size={16} />
                  Masuk
                </>
              )}
            </button>
          </form>

          {/* Platform info */}
          <div className="mt-5 p-3 bg-muted/60 rounded-xl border border-border/50">
            <p className="text-xs text-muted-foreground text-center leading-relaxed">
              Akses akan diarahkan otomatis sesuai peran: <span className="font-semibold text-foreground">Super Admin</span> &amp; <span className="font-semibold text-foreground">Admin</span> → Dashboard Admin &nbsp;·&nbsp; <span className="font-semibold text-foreground">Partner</span> → Dashboard Partner
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          <a href="/" className="hover:text-primary transition-colors">← Kembali ke Website</a>
        </p>
      </div>
    </div>
  );
}
